import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navigation: React.FC = () => {
  const { currentUser, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();

  const navLink = (to: string, label: string): React.ReactElement => {
    const isActive = location.pathname === to ||
      (to !== '/' && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-gray-700 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
      >
        {label}
      </Link>
    );
  };

  const mobileNavLink = (to: string, label: string): React.ReactElement => {
    const isActive = location.pathname === to ||
      (to !== '/' && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
          isActive
            ? 'bg-gray-700 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {label}
      </Link>
    );
  };

  return (
    <>
      <nav className="bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">

            {/* Logo + Desktop Links */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center flex-shrink-0">
                <img
                  src="/images/logo.svg"
                  alt="Chart2Video"
                  className="h-9 w-auto object-contain"
                  aria-label="Chart2Video Logo"
                />
              </Link>
              <div className="hidden md:flex ml-10 space-x-1">
                {navLink('/', 'Home')}
                {navLink('/documentation', 'Documentation')}
                {navLink('/datablog', 'DataBlog')}
              </div>
            </div>

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center">
              {currentUser ? (
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm truncate max-w-[180px]">
                    {currentUser.email}
                  </span>
                  <button
                    onClick={signOut}
                    className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/signin"
                    className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="btn-primary"
                    style={{ fontSize: 13, padding: '8px 16px', borderRadius: 6 }}
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Hamburger */}
            <div className="md:hidden flex items-center">
              <button
                type="button"
                className="text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white p-1 rounded"
                aria-controls="mobile-menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-700" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {mobileNavLink('/', 'Home')}
              {mobileNavLink('/documentation', 'Documentation')}
              {mobileNavLink('/datablog', 'DataBlog')}
              <div className="border-t border-gray-700 pt-2 mt-2">
                {currentUser ? (
                  <>
                    <span className="text-gray-400 block px-3 py-2 text-sm truncate">
                      {currentUser.email}
                    </span>
                    <button
                      onClick={() => { signOut(); setMobileMenuOpen(false); }}
                      className="text-gray-300 hover:bg-gray-700 hover:text-white block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    {mobileNavLink('/signin', 'Sign In')}
                    <Link
                      to="/signup"
                      className="btn-primary"
                      style={{ display: 'block', fontSize: 14, padding: '8px 14px', borderRadius: 6, marginTop: 4 }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Early Access Banner */}
      <div style={{ background: 'linear-gradient(90deg,#1c1008,#231408,#1c1008)', borderBottom: '1px solid rgba(242,100,25,0.25)' }}>
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center">
          <div className="inline-flex items-center gap-2.5">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full" style={{ background: 'var(--c2v-primary)', opacity: 0.6 }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: 'var(--c2v-primary)' }} />
            </span>
            <p className="text-xs text-left sm:text-center leading-snug m-0" style={{ color: 'rgba(253,215,185,0.95)' }}>
              <span className="font-semibold" style={{ color: 'var(--c2v-gold)' }}>Early Access</span>
              {' — '}We're actively building and improving Chart2Video. Thanks for being an early user! Expect rapid updates with many new charts and AI research & chart generation.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;